<template>
  <div>
    <div
      v-if="shown"
      class="is-flex has-background-light is-border">
      <div class="column columns is-vcentered is-marginless is-mobile is-padding-top-xxsmall is-padding-bottom-xxsmall">
        <div class="column is-narrow is-paddingless">
          <avatar :item="shown" />
        </div>
        <div
          class="column is-paddingless is-padding-left-xsmall"
          style="min-width: 0">
          <div class="truncate is-size-6 has-text-weight-medium">
            <div>
              <span class="is-uppercase">{{ shown.last_name }}</span> {{ shown.first_name }}
              <span
                v-if="shownTiersName"
                class="has-text-grey"> — {{ shownTiersName }}</span>
            </div>
          </div>
          <div
            v-if="selectedSubtitle"
            class="truncate is-size-7 has-text-grey">
            <div>{{ selectedSubtitle }}</div>
          </div>
        </div>
      </div>
      <div class="actionsOnRow actionsOnRow--vertical">
        <button
          type="button"
          class="tooltip is-tooltip-left actionsOnRow-btn--hoverIsDanger is-min-height-full"
          data-tooltip="Délier du référentiel"
          @click="$emit('unlink')">
          <span class="icon"><i class="fal fa-unlink"></i></span>
        </button>
      </div>
    </div>
    <div
      v-else-if="broken"
      class="is-flex has-background-light is-border">
      <div class="column columns is-vcentered is-marginless is-mobile is-padding-top-xxsmall is-padding-bottom-xxsmall">
        <div class="column is-narrow is-paddingless">
          <span class="icon is-medium has-text-danger">
            <i class="fal fa-lg fa-exclamation-triangle"></i>
          </span>
        </div>
        <div
          class="column is-paddingless is-padding-left-xsmall"
          style="min-width: 0">
          <div class="truncate is-size-6 has-text-weight-medium">
            <div>{{ (slotRef && slotRef.name) || 'Tiers inconnu' }}</div>
          </div>
          <div class="truncate is-size-7 has-text-danger">
            <div>Fiche supprimée du référentiel — déliez pour ressaisir</div>
          </div>
        </div>
      </div>
      <div class="actionsOnRow actionsOnRow--vertical">
        <button
          type="button"
          class="tooltip is-tooltip-left actionsOnRow-btn--hoverIsDanger is-min-height-full"
          data-tooltip="Délier du référentiel"
          @click="$emit('unlink')">
          <span class="icon"><i class="fal fa-unlink"></i></span>
        </button>
      </div>
    </div>
    <div
      v-else
      class="control">
      <multiselect
        v-model="searchModel"
        :placeholder="''"
        :options="options"
        :searchable="true"
        :internal-search="false"
        :custom-label="interlocuteurLabel"
        :close-on-select="true"
        :show-labels="false"
        :loading="loading"
        class="multiselect--option-ico"
        @search-change="search"
        @open="search('')"
        @input="onSelect">
        <span slot="noResult">Oups! Aucun tiers trouvé.</span>
        <span slot="noOptions">La liste est vide</span>
        <span
          slot="placeholder"
          class="multiselect__placeholder">{{ placeholder }}</span>
      </multiselect>
    </div>
  </div>
</template>

<script>
import { TIERS_RESOURCE, INTERLOCUTEURS_RESOURCE } from './tiersConstants'

export default {
  name: 'TiersSelector',
  props: {
    // interlocuteur sélectionné { ...interlocuteur, tiers } — null si non lié
    selected: {
      type: Object,
      default: null
    },
    // objet métier portant tiers_id / interlocuteur_id : permet au sélecteur
    // de précharger la carte tout seul quand l'emplacement est déjà lié
    slotRef: {
      type: Object,
      default: null
    },
    // types de tiers proposés à la recherche
    types: {
      type: Array,
      required: true
    },
    placeholder: {
      type: String,
      default: 'Rechercher au référentiel...'
    }
  },
  data () {
    return {
      searchModel: null,
      options: [],
      loading: false,
      internalSelected: null,
      // tiers_id présent mais fiche introuvable (supprimée) : la carte
      // « lien rompu » garde le bouton Délier accessible
      broken: false
    }
  },
  computed: {
    shown () {
      return this.selected || this.internalSelected
    },
    shownTiersName () {
      if (!this.shown || !this.shown.tiers) return null
      return this.shown.tiers.name || null
    },
    selectedSubtitle () {
      if (!this.shown) return null
      const tiers = this.shown.tiers || {}
      const parts = []
      if (tiers.address && tiers.address.city) parts.push(tiers.address.city)
      if (this.shown.phone || tiers.phone) parts.push(this.shown.phone || tiers.phone)
      return parts.join(' · ') || null
    }
  },
  watch: {
    'slotRef.tiers_id': {
      immediate: true,
      async handler (tiersId) {
        if (!tiersId) {
          this.internalSelected = null
          this.broken = false
          return
        }
        if (this.shown && this.shown.tiers && this.shown.tiers.pk === tiersId) return
        try {
          let { data } = await this.$api.getOne(TIERS_RESOURCE, tiersId)
          const { interlocuteurs, ...tiers } = data
          const interlocuteur = (interlocuteurs || [])
            .find(i => i.pk === this.slotRef.interlocuteur_id)
          this.internalSelected = interlocuteur ? { ...interlocuteur, tiers } : null
          this.broken = !interlocuteur
        } catch (e) {
          this.internalSelected = null
          this.broken = !!(e.response && e.response.status === 404)
        }
      }
    },
    selected (value) {
      if (!value) this.internalSelected = null
      else this.broken = false
    }
  },
  created () {
    this.search('')
  },
  methods: {
    interlocuteurLabel (interlocuteur) {
      let label = `${(interlocuteur.last_name || '').toUpperCase()} ${interlocuteur.first_name || ''}`.trim()
      if (interlocuteur.tiers && interlocuteur.tiers.name) label += ` — ${interlocuteur.tiers.name}`
      if (interlocuteur.tiers && interlocuteur.tiers.address && interlocuteur.tiers.address.city) label += `, ${interlocuteur.tiers.address.city}`
      return label
    },
    async search (query) {
      this.loading = true
      try {
        let { data } = await this.$api.get(INTERLOCUTEURS_RESOURCE, {
          searchable_name: query || null,
          type: this.types,
          per_page: 20
        })
        this.options = data.items
      } catch (e) {
      }
      this.loading = false
    },
    /**
     * La sélection est émise puis le champ se vide : l'état « lié » est porté
     * par le parent (carte + Délier), jamais par le multiselect
     */
    onSelect (interlocuteur) {
      if (!interlocuteur) return
      this.$emit('select', interlocuteur)
      this.$nextTick(() => { this.searchModel = null })
    }
  }
}
</script>
